import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LucideDynamicIcon } from "@lucide/angular";
import { AuthService, User } from "../core/auth.service";
import { hubLabel } from "../core/hubs";
import { LanguageService } from "../core/language.service";
import { ToastService } from "../core/toast.service";
import {
  FINAL_ASSESSMENT_PASS_RATE,
  VICTORY_DISSOLUTION_DAYS,
  type CrewVictoryBoard,
  type CrewVictoryMember,
  type CrewVictoryStatus,
  crewCompletionScore,
  crewFinalAssessmentScore,
  declareCrewVictory,
  loadCrewVictoryBoard,
} from "../game/crew-victory";
import { HubFlagComponent } from "../shared/hub-flag.component";
import { LanguageSelectorComponent } from "../shared/language-selector.component";
import {
  badgeClasses,
  buttonClasses,
  cardClasses,
  progressBarStyle,
} from "../shared/ui";

type WinnerTab = "overview" | "player" | "crew";

@Component({
  selector: "app-winner-page",
  standalone: true,
  imports: [
    CommonModule,
    HubFlagComponent,
    LanguageSelectorComponent,
    LucideDynamicIcon,
  ],
  template: `
    <div
      *ngIf="loading"
      class="min-h-screen flex items-center justify-center ocean-gradient"
    >
      <svg
        lucideIcon="loader-circle"
        class="h-8 w-8 animate-spin text-primary-foreground"
      ></svg>
    </div>

    <div *ngIf="!loading" class="min-h-screen ocean-gradient">
      <header
        class="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div class="container max-w-5xl mx-auto px-4 py-4">
          <div class="flex flex-wrap items-center justify-between gap-4">
            <div class="flex min-w-0 items-center gap-4">
              <button
                [class]="buttonClasses('ghost', 'icon')"
                (click)="router.navigateByUrl('/')"
              >
                <svg lucideIcon="arrow-left" class="h-5 w-5"></svg>
              </button>
              <div class="flex min-w-0 items-center gap-3">
                <div class="p-2 rounded-full bg-secondary/20">
                  <svg
                    lucideIcon="crown"
                    class="h-6 w-6 text-secondary-foreground"
                  ></svg>
                </div>
                <div class="min-w-0">
                  <h1 class="text-xl font-bold text-foreground">
                    {{ language.t("winner.title") }}
                  </h1>
                  <p class="truncate text-sm text-muted-foreground">
                    {{ language.t("winner.subtitle") }}
                  </p>
                </div>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <app-language-selector
                [compact]="true"
                className="bg-background/95"
              />
              <button
                [class]="buttonClasses('outline', 'sm')"
                (click)="fetchData()"
              >
                <svg lucideIcon="rotate-ccw" class="h-4 w-4"></svg>
                {{ language.t("winner.refresh") }}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main class="container max-w-5xl mx-auto px-4 py-8">
        <section
          *ngIf="errorMessage"
          [class]="cardClasses('mb-6 border-destructive/40 bg-destructive/10 p-4')"
        >
          <div class="flex gap-3">
            <svg
              lucideIcon="circle-x"
              class="mt-0.5 h-5 w-5 shrink-0 text-destructive"
            ></svg>
            <div>
              <p class="text-sm font-semibold text-foreground">
                {{ language.t("winner.loadErrorTitle") }}
              </p>
              <p class="mt-1 text-sm text-muted-foreground">
                {{ errorMessage }}
              </p>
            </div>
          </div>
        </section>

        <section
          *ngIf="winner as winningEntry; else waitingForWinner"
          [class]="
            cardClasses(
              'mb-6 overflow-hidden border-2 border-secondary bg-card/95 shadow-xl'
            )
          "
        >
          <div
            class="border-b border-secondary/30 bg-secondary/15 px-6 py-8 text-center"
          >
            <div
              class="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full bg-secondary text-secondary-foreground shadow"
            >
              <svg lucideIcon="crown" class="h-9 w-9"></svg>
            </div>
            <p class="text-sm font-semibold uppercase tracking-normal text-secondary-foreground">
              {{
                language.t(
                  winningEntry.is_solo
                    ? "winner.soloDeclared"
                    : "winner.declared"
                )
              }}
            </p>
            <h2 class="mt-2 text-3xl font-bold text-foreground">
              {{ winningEntry.crew_name }}
            </h2>
            <p class="mt-2 text-sm text-muted-foreground">
              {{ language.t("winner.declaredOn") }}
              {{ winningEntry.declared_at | date: "medium" }}
            </p>
            <p
              *ngIf="winningEntry.victory_dissolves_at"
              class="mt-2 text-sm font-medium text-muted-foreground"
            >
              {{ language.t("winner.dissolvesOn") }}
              {{ winningEntry.victory_dissolves_at | date: "mediumDate" }}
            </p>
          </div>

          <div class="grid gap-4 p-6 md:grid-cols-3">
            <div class="rounded-md border border-border bg-background/70 p-4">
              <p class="text-2xl font-bold text-primary">
                {{ winningEntry.member_count }}
              </p>
              <p class="text-sm text-muted-foreground">
                {{
                  winningEntry.is_solo
                    ? language.t("winner.solo")
                    : language.t("winner.qualifiedMembers")
                }}
              </p>
            </div>
            <div class="rounded-md border border-border bg-background/70 p-4">
              <p class="text-2xl font-bold text-accent">
                {{ winningEntry.total_islands }}
              </p>
              <p class="text-sm text-muted-foreground">
                {{ language.t("winner.islandsEach") }}
              </p>
            </div>
            <div class="rounded-md border border-border bg-background/70 p-4">
              <p class="text-2xl font-bold text-secondary-foreground">
                {{ winningEntry.total_score }}
              </p>
              <p class="text-sm text-muted-foreground">
                {{
                  language.t(
                    winningEntry.is_solo
                      ? "winner.playerPoints"
                      : "winner.crewPoints"
                  )
                }}
              </p>
            </div>
          </div>

          <div
            *ngIf="winningEntry.victory_dissolved"
            class="border-t border-secondary/30 bg-primary/10 p-6 text-center"
          >
            <h3 class="text-xl font-bold text-foreground">
              {{ language.t("winner.dissolvedTitle") }}
            </h3>
            <p class="mx-auto mt-2 max-w-2xl text-sm leading-6 text-muted-foreground">
              {{ language.t("winner.dissolvedDescription") }}
            </p>
          </div>
        </section>

        <ng-template #waitingForWinner>
          <section
            [class]="
              cardClasses(
                'mb-6 overflow-hidden border-2 border-primary/25 bg-card/95 shadow-xl'
              )
            "
          >
            <div class="grid gap-6 p-6 md:grid-cols-[1.2fr_0.8fr]">
              <div>
                <span [class]="badgeClasses('secondary', 'mb-4')">
                  {{ language.t("winner.noWinnerBadge") }}
                </span>
                <h2 class="text-3xl font-bold text-foreground">
                  {{ language.t("winner.waitingTitle") }}
                </h2>
                <p class="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
                  {{
                    language.t("winner.waitingDescription", {
                      rate: finalAssessmentPassRate,
                    })
                  }}
                </p>
              </div>
              <div class="rounded-md border border-border bg-background/70 p-4">
                <p class="text-sm font-semibold text-foreground">
                  {{ language.t("winner.requirementsTitle") }}
                </p>
                <div class="mt-4 space-y-3 text-sm text-muted-foreground">
                  <div class="flex items-start gap-2">
                    <svg lucideIcon="users" class="mt-0.5 h-4 w-4"></svg>
                    <span>{{ language.t("winner.requirement.validEntry") }}</span>
                  </div>
                  <div class="flex items-start gap-2">
                    <svg lucideIcon="map-pin" class="mt-0.5 h-4 w-4"></svg>
                    <span>{{ language.t("winner.requirement.islands") }}</span>
                  </div>
                  <div class="flex items-start gap-2">
                    <svg
                      lucideIcon="clipboard-list"
                      class="mt-0.5 h-4 w-4"
                    ></svg>
                    <span>{{ language.t("winner.requirement.final") }}</span>
                  </div>
                  <div class="flex items-start gap-2">
                    <svg lucideIcon="crown" class="mt-0.5 h-4 w-4"></svg>
                    <span>{{ language.t("winner.requirement.declare") }}</span>
                  </div>
                </div>
                <p class="mt-4 text-xs leading-5 text-muted-foreground">
                  {{
                    language.t("winner.dissolutionNote", {
                      days: victoryDissolutionDays,
                    })
                  }}
                </p>
              </div>
            </div>
          </section>
        </ng-template>

        <section
          *ngIf="currentUserCrew"
          [class]="cardClasses('mb-6 border-primary/30 bg-primary/5 p-5')"
        >
          <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p class="text-sm font-semibold text-foreground">
                {{ currentUserEntryLabel }}
              </p>
              <p class="mt-1 text-sm text-muted-foreground">
                {{ currentUserCrewStatusText }}
              </p>
            </div>
            <div class="flex flex-col items-start gap-2 md:items-end">
              <button
                *ngIf="currentUserNeedsFinalAssessment"
                [class]="buttonClasses('default', 'sm')"
                (click)="router.navigateByUrl('/self-assessment')"
              >
                <svg lucideIcon="clipboard-list" class="h-4 w-4"></svg>
                {{ language.t("winner.finalReassessment") }}
              </button>
              <button
                [class]="declareVictoryButtonClasses"
                [disabled]="!currentUserCanDeclareVictory || declaringVictory"
                (click)="declareVictory()"
              >
                <svg
                  *ngIf="declaringVictory"
                  lucideIcon="loader-circle"
                  class="h-4 w-4 animate-spin"
                ></svg>
                <svg
                  *ngIf="!declaringVictory"
                  lucideIcon="crown"
                  class="h-4 w-4"
                ></svg>
                {{ declareVictoryLabel }}
              </button>
              <p class="max-w-xs text-xs leading-5 text-muted-foreground md:text-right">
                {{ declareVictoryHint }}
              </p>
              <button
                *ngIf="currentUserFinalAssessmentLocked"
                [class]="buttonClasses('outline', 'sm')"
                disabled
              >
                <svg lucideIcon="lock" class="h-4 w-4"></svg>
                {{ language.t("winner.finalReassessmentLocked") }}
              </button>
              <p
                *ngIf="currentUserFinalAssessmentLocked"
                class="max-w-xs text-xs leading-5 text-muted-foreground md:text-right"
              >
                {{ language.t("winner.finalLockedDescription") }}
              </p>
            </div>
          </div>
        </section>

        <div class="mb-6 grid w-full grid-cols-3 rounded-md bg-muted p-1">
          <button
            type="button"
            (click)="activeTab = 'overview'"
            [class]="tabClass('overview')"
          >
            {{ language.t("winner.tabs.overview") }}
          </button>
          <button
            type="button"
            (click)="activeTab = 'player'"
            [class]="tabClass('player')"
          >
            {{ language.t("winner.tabs.player") }}
          </button>
          <button
            type="button"
            (click)="activeTab = 'crew'"
            [class]="tabClass('crew')"
          >
            {{ language.t("winner.tabs.crew") }}
          </button>
        </div>

        <section *ngIf="activeTab === 'overview'" [class]="cardClasses('bg-card/95')">
          <header class="flex flex-col space-y-1.5 p-6">
            <h2
              class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2"
            >
              <svg lucideIcon="trophy" class="h-5 w-5 text-primary"></svg>
              {{ language.t("winner.progressTitle") }}
            </h2>
            <p class="text-sm text-muted-foreground">
              {{ language.t("winner.progressLiveDescription") }}
            </p>
          </header>

          <div class="p-6 pt-0">
            <div
              *ngIf="crews.length === 0"
              class="py-8 text-center text-muted-foreground"
            >
              <p>{{ language.t("winner.noCrews") }}</p>
            </div>

            <div class="space-y-4">
              <article
                *ngFor="let crew of crews"
                class="rounded-md border border-border bg-background/75 p-4"
              >
                <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-2">
                      <h3 class="truncate text-lg font-semibold text-foreground">
                        {{ crew.crew_name }}
                      </h3>
                      <span
                        *ngIf="crew.winner_declared"
                        [class]="badgeClasses('secondary')"
                        >{{ language.t("winner.winnerBadge") }}</span
                      >
                      <span [class]="badgeClasses('outline')">
                        {{
                          crew.is_solo
                            ? language.t("winner.solo")
                            : language.t("winner.members", {
                                count: crew.member_count,
                              })
                        }}
                      </span>
                    </div>
                    <p class="mt-1 text-sm text-muted-foreground">
                      {{ language.t("winner.points", { points: crew.total_score }) }}
                    </p>
                  </div>
                  <div class="grid w-full gap-3 md:w-72">
                    <div>
                      <div class="mb-1 flex justify-between text-xs">
                        <span class="text-muted-foreground">
                          {{ language.t("winner.islands") }}
                        </span>
                        <span class="font-medium text-foreground">
                          {{ crewCompletionScore(crew) }}%
                        </span>
                      </div>
                      <div
                        class="relative h-2 w-full overflow-hidden rounded-full bg-secondary"
                      >
                        <div
                          class="h-full w-full bg-primary transition-all"
                          [ngStyle]="progressBarStyle(crewCompletionScore(crew))"
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div class="mb-1 flex justify-between text-xs">
                        <span class="text-muted-foreground">
                          {{ language.t("winner.finalCheck") }}
                        </span>
                        <span class="font-medium text-foreground">
                          {{ crewFinalAssessmentScore(crew) }}%
                        </span>
                      </div>
                      <div
                        class="relative h-2 w-full overflow-hidden rounded-full bg-secondary"
                      >
                        <div
                          class="h-full w-full bg-accent transition-all"
                          [ngStyle]="
                            progressBarStyle(crewFinalAssessmentScore(crew))
                          "
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-4 grid gap-2 md:grid-cols-2">
                  <div
                    *ngFor="let member of crew.members"
                    class="rounded-md border border-border/70 bg-card/60 p-3"
                  >
                    <ng-container
                      [ngTemplateOutlet]="memberSummary"
                      [ngTemplateOutletContext]="{ member: member, top: false }"
                    />
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section
          *ngIf="activeTab === 'player'"
          [class]="cardClasses('bg-card/95 p-6')"
        >
          <ng-container *ngIf="winnerTopMember as playerWinner; else noPlayerWinner">
            <div class="grid gap-6 md:grid-cols-[0.8fr_1.2fr] md:items-center">
              <div class="text-center">
                <div
                  class="mx-auto grid h-24 w-24 place-items-center overflow-hidden rounded-full bg-primary/15 text-2xl font-bold text-primary"
                >
                  <img
                    *ngIf="playerWinner.avatar_url"
                    [src]="playerWinner.avatar_url"
                    alt=""
                    class="h-full w-full object-cover"
                  />
                  <span *ngIf="!playerWinner.avatar_url">
                    {{ initials(playerWinner) }}
                  </span>
                </div>
                <h2 class="mt-4 text-2xl font-bold text-foreground">
                  {{ playerWinner.display_name || language.t("map.explorer") }}
                </h2>
                <p class="mt-1 text-sm text-muted-foreground">
                  {{
                    winner?.is_solo
                      ? language.t("winner.playerWinnerDescriptionSolo")
                      : language.t("winner.playerWinnerDescriptionCrew")
                  }}
                </p>
              </div>
              <div class="grid gap-4 sm:grid-cols-3">
                <div class="rounded-md border border-border bg-background/70 p-4">
                  <p class="text-2xl font-bold text-primary">
                    {{ playerWinner.total_score }}
                  </p>
                  <p class="text-sm text-muted-foreground">
                    {{ language.t("winner.playerPoints") }}
                  </p>
                </div>
                <div class="rounded-md border border-border bg-background/70 p-4">
                  <p class="text-2xl font-bold text-accent">
                    {{ playerWinner.islands_completed }}/{{
                      playerWinner.total_islands
                    }}
                  </p>
                  <p class="text-sm text-muted-foreground">
                    {{ language.t("winner.islands") }}
                  </p>
                </div>
                <div class="rounded-md border border-border bg-background/70 p-4">
                  <p class="text-2xl font-bold text-secondary-foreground">
                    {{
                      playerWinner.final_assessment_rate === null
                        ? "-"
                        : (playerWinner.final_assessment_rate | number: "1.0-0") +
                          "%"
                    }}
                  </p>
                  <p class="text-sm text-muted-foreground">
                    {{ language.t("winner.finalCheck") }}
                  </p>
                </div>
              </div>
            </div>
          </ng-container>
          <ng-template #noPlayerWinner>
            <div class="py-8 text-center text-muted-foreground">
              <p>{{ language.t("winner.noPlayerWinner") }}</p>
            </div>
          </ng-template>
        </section>

        <section
          *ngIf="activeTab === 'crew'"
          [class]="cardClasses('bg-card/95')"
        >
          <header class="flex flex-col space-y-1.5 p-6">
            <h2
              class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2"
            >
              <svg lucideIcon="medal" class="h-5 w-5 text-primary"></svg>
              {{ language.t("winner.crewRankingTitle") }}
            </h2>
          </header>
          <div class="p-6 pt-0">
            <div
              *ngIf="!winner"
              class="py-8 text-center text-muted-foreground"
            >
              <p>{{ language.t("winner.noCrewWinner") }}</p>
            </div>
            <div
              *ngIf="winner?.is_solo"
              class="py-8 text-center text-muted-foreground"
            >
              <p>{{ language.t("winner.soloWinnerNoCrew") }}</p>
            </div>
            <div
              *ngIf="winner && !winner.is_solo"
              class="grid gap-3 md:grid-cols-2"
            >
              <div
                *ngFor="let member of winnerMembersByScore; let index = index"
                class="rounded-md border border-border bg-background/70 p-4"
              >
                <div class="mb-2 flex items-center justify-between">
                  <span [class]="rankClass(index + 1)">#{{ index + 1 }}</span>
                  <span
                    *ngIf="index === 0"
                    [class]="badgeClasses('secondary')"
                    >{{ language.t("winner.topScorerBadge") }}</span
                  >
                </div>
                <ng-container
                  [ngTemplateOutlet]="memberSummary"
                  [ngTemplateOutletContext]="{ member: member, top: index === 0 }"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>

    <ng-template #memberSummary let-member="member">
      <div class="flex items-start gap-3">
        <div
          class="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-primary/15 text-sm font-semibold text-primary"
        >
          <img
            *ngIf="member.avatar_url"
            [src]="member.avatar_url"
            alt=""
            class="h-full w-full object-cover"
          />
          <span *ngIf="!member.avatar_url">
            {{ initials(member) }}
          </span>
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <p class="truncate text-sm font-semibold text-foreground">
              {{ member.display_name || language.t("map.explorer") }}
            </p>
            <app-hub-flag [hub]="member.hub" size="sm" />
            <span class="text-xs text-muted-foreground">
              {{ hubLabel(member.hub) }}
            </span>
          </div>
          <div class="mt-2 grid gap-1 text-xs text-muted-foreground">
            <p>
              {{ language.t("winner.memberIslands") }}:
              <span class="font-medium text-foreground">
                {{ member.islands_completed }}/{{ member.total_islands }}
              </span>
            </p>
            <p>
              {{ language.t("winner.memberFinal") }}:
              <span
                [ngClass]="
                  member.final_assessment_passed
                    ? 'text-accent'
                    : 'text-muted-foreground'
                "
                class="font-medium"
              >
                {{ finalAssessmentLabel(member) }}
              </span>
            </p>
            <p>
              {{ language.t("winner.memberDeclaration") }}:
              <span
                [ngClass]="
                  member.victory_declared
                    ? 'text-accent'
                    : 'text-muted-foreground'
                "
                class="font-medium"
              >
                {{
                  member.victory_declared
                    ? language.t("winner.declaredStatus")
                    : language.t("winner.notDeclaredStatus")
                }}
              </span>
            </p>
            <p>
              {{ language.t("common.points") }}:
              <span class="font-medium text-foreground">
                {{ member.total_score }}
              </span>
            </p>
            <p *ngIf="member.daily_streak_bonus_score > 0">
              {{ language.t("winner.memberStreakBonus") }}:
              <span class="font-medium text-orange-600">
                +{{ member.daily_streak_bonus_score }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </ng-template>
  `,
})
export class WinnerPageComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  readonly router = inject(Router);
  readonly language = inject(LanguageService);
  private readonly toast = inject(ToastService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  user: User | null = null;
  board: CrewVictoryBoard | null = null;
  loading = true;
  errorMessage = "";
  activeTab: WinnerTab = "overview";
  finalAssessmentPassRate = FINAL_ASSESSMENT_PASS_RATE;
  victoryDissolutionDays = VICTORY_DISSOLUTION_DAYS;
  declaringVictory = false;
  private authSubscription?: Subscription;
  private languageSubscription?: Subscription;
  private loadedForUserId: string | null = null;

  ngOnInit() {
    this.authSubscription = this.auth.state$.subscribe((state) => {
      this.user = state.user;
      this.changeDetector.detectChanges();

      if (state.loading) return;

      if (!state.user) {
        void this.router.navigateByUrl("/auth");
        return;
      }

      if (this.loadedForUserId !== state.user.id) {
        this.loadedForUserId = state.user.id;
        void this.fetchData();
      }
    });

    this.languageSubscription = this.language.language$.subscribe(() => {
      this.changeDetector.detectChanges();
    });
  }

  ngOnDestroy() {
    this.authSubscription?.unsubscribe();
    this.languageSubscription?.unsubscribe();
  }

  get winner() {
    return this.board?.winner ?? null;
  }

  get crews() {
    return this.board?.crews ?? [];
  }

  get currentUserCrew() {
    return this.board?.currentUserCrew ?? null;
  }

  get winnerMembersByScore() {
    return [...(this.winner?.members ?? [])].sort(
      (left, right) => right.total_score - left.total_score,
    );
  }

  get winnerTopMember() {
    return this.winnerMembersByScore[0] ?? null;
  }

  get currentUserFinalStatus() {
    return (
      this.currentUserCrew?.members.find(
        (member) => member.user_id === this.user?.id,
      ) ?? null
    );
  }

  get currentUserNeedsFinalAssessment() {
    return Boolean(
      this.currentUserCrew?.completed_all_islands &&
        !this.currentUserFinalStatus?.final_assessment_passed,
    );
  }

  get currentUserCanDeclareVictory() {
    return Boolean(
      this.currentUserCrew?.final_assessment_complete &&
        !this.currentUserCrew.winner_declared &&
        this.currentUserFinalStatus?.final_assessment_passed &&
        !this.currentUserFinalStatus.victory_declared,
    );
  }

  get currentUserFinalAssessmentLocked() {
    return Boolean(
      this.currentUserCrew && !this.currentUserCrew.completed_all_islands,
    );
  }

  get declareVictoryLabel() {
    if (this.declaringVictory) return this.language.t("winner.declaringVictory");
    if (
      this.currentUserFinalStatus?.victory_declared ||
      this.currentUserCrew?.winner_declared
    ) {
      return this.language.t("winner.declareAlreadyDone");
    }
    if (this.currentUserCanDeclareVictory) {
      return this.language.t("winner.declareVictory");
    }
    return this.language.t("winner.declareLocked");
  }

  get declareVictoryHint() {
    const crew = this.currentUserCrew;
    if (!crew) return "";
    if (crew.victory_dissolved) {
      return this.language.t("winner.declareDissolvedHint");
    }
    if (crew.winner_declared) {
      return this.language.t("winner.declareWinnerHint");
    }
    if (this.currentUserCanDeclareVictory) {
      return this.language.t("winner.declareReadyHint");
    }
    if (this.currentUserFinalStatus?.victory_declared) {
      return this.language.t("winner.declareWaitingTeamHint");
    }
    if (!crew.completed_all_islands) {
      return this.language.t("winner.declareLockedIslandsHint");
    }
    if (this.currentUserNeedsFinalAssessment) {
      return this.language.t("winner.declareLockedAssessmentHint");
    }
    if (!crew.final_assessment_complete) {
      return this.language.t("winner.declareLockedTeamHint");
    }
    return this.language.t("winner.declareLockedHint");
  }

  get declareVictoryButtonClasses() {
    const readyClass =
      "min-w-48 justify-center bg-gradient-to-r from-secondary via-primary to-accent text-primary-foreground shadow-lg shadow-primary/25 hover:scale-[1.02] hover:shadow-xl disabled:opacity-90";
    const lockedClass =
      "min-w-48 justify-center border-secondary/50 bg-secondary/10 text-foreground/70 disabled:opacity-80";
    return buttonClasses(
      this.currentUserCanDeclareVictory ? "default" : "outline",
      "sm",
      this.currentUserCanDeclareVictory ? readyClass : lockedClass,
    );
  }

  get currentUserEntryLabel() {
    const entry = this.currentUserCrew;
    if (!entry) return "";
    return this.language.t(entry.is_solo ? "winner.yourEntry" : "winner.yourCrew", {
      name: entry.crew_name,
    });
  }

  get currentUserCrewStatusText() {
    const crew = this.currentUserCrew;
    if (!crew) return "";
    if (crew.victory_dissolved)
      return this.language.t("winner.dissolvedDescription");
    if (crew.winner_declared)
      return this.language.t("winner.currentUserStatusWinner");
    if (!crew.completed_all_islands) {
      return this.language.t("winner.currentUserStatusProgress", {
        percent: crewCompletionScore(crew),
      });
    }
    if (this.currentUserNeedsFinalAssessment) {
      return this.language.t("winner.currentUserStatusNeedsAssessment");
    }
    if (this.currentUserCanDeclareVictory) {
      return this.language.t("winner.currentUserStatusCanDeclare");
    }
    if (
      crew.victory_declaration_open &&
      this.currentUserFinalStatus?.victory_declared
    ) {
      return this.language.t("winner.currentUserStatusDeclared");
    }
    return this.language.t("winner.currentUserStatusWaiting");
  }

  async declareVictory() {
    if (
      !this.currentUserCrew ||
      !this.user ||
      !this.currentUserCanDeclareVictory ||
      this.declaringVictory
    )
      return;

    this.declaringVictory = true;
    this.changeDetector.detectChanges();

    try {
      const result = await declareCrewVictory(
        this.currentUserCrew.crew_id,
        this.user.id,
      );
      this.toast.success(
        this.language.t(
          result.storedLocally
            ? "winner.declareLocalSuccess"
            : "winner.declareSuccess",
        ),
      );
      await this.fetchData();
    } catch (error) {
      console.error("Error declaring victory:", error);
      this.toast.error(this.language.t("winner.loadErrorTitle"));
    } finally {
      this.declaringVictory = false;
      this.changeDetector.detectChanges();
    }
  }

  async fetchData() {
    if (!this.user) return;

    this.loading = true;
    this.errorMessage = "";
    this.changeDetector.detectChanges();

    try {
      this.board = await loadCrewVictoryBoard(this.user.id);
    } catch (error) {
      console.error("Error loading winner page:", error);
      this.errorMessage =
        error instanceof Error ? error.message : "Unexpected error";
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  initials(member: CrewVictoryMember) {
    const name = member.display_name?.trim() || this.language.t("map.explorer");
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("");
  }

  finalAssessmentLabel(member: CrewVictoryMember) {
    if (
      member.final_assessment_score === null ||
      member.final_assessment_total === null ||
      member.final_assessment_rate === null
    ) {
      return this.language.t("winner.pending");
    }

    return `${member.final_assessment_score}/${member.final_assessment_total} (${Math.round(member.final_assessment_rate)}%)`;
  }

  tabClass(value: WinnerTab) {
    return [
      "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all",
      this.activeTab === value
        ? "bg-background text-foreground shadow-sm"
        : "text-muted-foreground hover:text-foreground",
    ].join(" ");
  }

  rankClass(rank: number) {
    if (rank === 1)
      return "grid h-8 w-8 place-items-center rounded-full bg-secondary text-sm font-bold text-secondary-foreground";
    if (rank === 2)
      return "grid h-8 w-8 place-items-center rounded-full bg-muted text-sm font-bold text-foreground";
    if (rank === 3)
      return "grid h-8 w-8 place-items-center rounded-full bg-primary/15 text-sm font-bold text-primary";
    return "grid h-8 w-8 place-items-center rounded-full bg-muted text-sm font-bold text-foreground";
  }

  crewCompletionScore = crewCompletionScore;
  crewFinalAssessmentScore = crewFinalAssessmentScore;
  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  badgeClasses = badgeClasses;
  hubLabel = hubLabel;
  progressBarStyle = progressBarStyle;
}
